import { MapPin, Clock, MessageSquare, Calendar, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const locationData = {
  "Carleton Place": {
    image: "https://customer-assets.emergentagent.com/job_lauzon-care/artifacts/f3ty3lwt_p2%20sept2022.jpg",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2819.844!2d-76.1422!3d45.1381!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cd208a8a7c4e8e5%3A0x0!2s163%20Bridge%20St%2C%20Carleton%20Place%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890",
    directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=163+Bridge+Street+Carleton+Place+ON"
  },
  "Kanata": {
    image: "https://customer-assets.emergentagent.com/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/axpoecd5_image.png",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2803.123!2d-75.9103!3d45.3025!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cd1ff7e5e9e9e9e%3A0x0!2s320%20March%20Road%2C%20Kanata%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890",
    directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=320+March+Road+Kanata+ON"
  },
  "Ottawa": {
    image: "https://customer-assets.emergentagent.com/job_wellness-appts-1/artifacts/3cz1tl30_ottawa%20renos%202026.jpg",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2802.456!2d-75.7321!3d45.3543!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cd1fe9e9e9e9e9e%3A0x0!2s1390%20Prince%20of%20Wales%20Drive%2C%20Ottawa%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890",
    directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=1390+Prince+of+Wales+Drive+Ottawa+ON"
  }
};

const LocationCard = ({ location, t, language, openBookingModal }) => {
  const data = locationData[location.name] || {};
  
  return (
    <div
      className="location-card-glass rounded-xl overflow-hidden"
      data-testid={`location-card-${location.id}`}
    >
      {/* Clinic Image */}
      <div className="aspect-video overflow-hidden bg-muted img-zoom">
        <img
          src={data.image}
          alt={`${location.name} clinic`}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="p-6">
        <h3
          className="text-2xl font-semibold mb-4"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {location.name}
        </h3>

        <div className="space-y-3 mb-5">
          <div className="flex items-start gap-3 text-muted-foreground group">
            <div className="icon-container p-1.5 rounded-lg bg-primary/5">
              <MapPin className="w-4 h-4 text-primary shrink-0" />
            </div>
            <span className="text-sm">{location.address}</span>
          </div>
          <div className="flex items-start gap-3 text-muted-foreground group">
            <div className="icon-container p-1.5 rounded-lg bg-primary/5">
              <Clock className="w-4 h-4 text-primary shrink-0" />
            </div>
            <span className="text-sm">{location.hours}</span>
          </div>
          <div className="flex items-center gap-3 text-muted-foreground group">
            <div className="icon-container p-1.5 rounded-lg bg-primary/5">
              <MessageSquare className="w-4 h-4 text-primary shrink-0" />
            </div>
            <a
              href={`sms:${location.phone.replace(/[^0-9]/g, '')}`}
              className="text-sm hover:text-primary transition-colors underline-grow"
            >
              {language === "fr" ? "Textez-nous:" : "Text us:"} {location.phone}
            </a>
          </div>
        </div>

        {/* Google Map */}
        <div className="map-container mb-5 h-40">
          <iframe
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(location.address)}&zoom=15`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`${location.name} map`}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => openBookingModal && openBookingModal()}
            className="flex-1"
            data-testid={`book-now-${location.name.toLowerCase().replace(' ', '-')}`}
          >
            <Button className="w-full btn-accent btn-shine ripple flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{t("locations.bookOnline")}</span>
            </Button>
          </button>
          {data.directionsUrl && (
            <a
              href={data.directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0"
              title={language === "fr" ? "Itinéraire" : "Get Directions"}
            >
              <Button variant="outline" size="icon" className="h-full aspect-square hover:bg-primary/10 hover:border-primary">
                <Navigation className="w-4 h-4" />
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const LocationsSection = ({ locations, openBookingModal }) => {
  const { t, language } = useLanguage();
  
  return (
    <section
      id="locations"
      className="section-padding bg-muted"
      data-testid="locations-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="font-mono-label text-xs text-primary tracking-widest">
            {t("locations.title")}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("locations.heading")} <span className="italic text-primary">{t("locations.headingAccent")}</span>
          </h2>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            {t("locations.description")}
          </p>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {locations.map((location) => (
            <LocationCard key={location.id} location={location} t={t} language={language} openBookingModal={openBookingModal} />
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 p-6 glass-card-warm rounded-xl text-center">
          <p className="text-muted-foreground">
            <strong className="text-foreground">{t("locations.easyBooking")}</strong> — {t("locations.easyBookingDesc")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
